import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import { API_HOST } from "../constants";
import { SEOHead } from "../components/SEOHead";
import { generateWebSiteSchema, generateOrganizationSchema } from "../utils/seo";
import { useUser } from "../contexts/UserContext";
import { AssessmentQuizModal } from "../components/assessment/AssessmentQuizModal";
import { CONCEPT_TAXONOMY } from "../data/ch_concepts";
import {
  BookOpen,
  ArrowRight,
  PlayCircle,
  Target,
  MessageSquare,
  Database,
  Building2,
  Briefcase,
  Users,
  CheckCircle2,
  Send,
  Terminal,
  Brain,
  GitBranch,
  Sparkles,
  HelpCircle,
  BookX,
  Server,
  GraduationCap,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// ─── Mock UI building blocks ────────────────────────────────────────────────

function MockBrowserFrame({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-neutral-border shadow-2xl overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 px-4 py-2.5 bg-neutral-border/30 border-b border-neutral-border">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
      </div>
      {children}
    </div>
  );
}

/** Подсветка как в Monaco (clickhouse-analyst-light): белый фон, зелёные keyword/number */
function SqlKw({ children }: { children: React.ReactNode }) {
  return <span className="text-[#0abf53] font-bold">{children}</span>;
}
function SqlFn({ children }: { children: React.ReactNode }) {
  return <span className="text-[#067934]">{children}</span>;
}
function SqlStr({ children }: { children: React.ReactNode }) {
  return <span className="text-[#FF8C00]">{children}</span>;
}
function SqlId({ children }: { children: React.ReactNode }) {
  return <span className="text-[#00112c]">{children}</span>;
}
function SqlNum({ children }: { children: React.ReactNode }) {
  return <span className="text-[#0abf53]">{children}</span>;
}

function MockSqlSurface({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-xl border border-neutral-border/60 bg-white p-3 font-mono text-xs leading-relaxed text-[#00112c] shadow-[inset_0_1px_0_rgba(0,0,0,0.03)] whitespace-pre overflow-x-auto min-w-0 [tab-size:2] ${className}`}
    >
      {children}
    </div>
  );
}

function MockSqlHitsQuery() {
  return (
    <MockSqlSurface>
      <SqlKw>SELECT</SqlKw>
      {"\n  "}
      <SqlId>domain</SqlId>,{"\n  "}
      <SqlFn>count</SqlFn>() <SqlKw>AS</SqlKw> <SqlId>visits</SqlId>,{"\n  "}
      <SqlFn>uniq</SqlFn>(<SqlId>user_id</SqlId>) <SqlKw>AS</SqlKw> <SqlId>users</SqlId>
      {"\n"}
      <SqlKw>FROM</SqlKw> <SqlId>hits</SqlId>
      {"\n"}
      <SqlKw>WHERE</SqlKw> <SqlId>event_date</SqlId> <SqlKw>&gt;=</SqlKw> <SqlStr>&apos;2024-01-01&apos;</SqlStr>
      {"\n"}
      <SqlKw>GROUP BY</SqlKw> <SqlId>domain</SqlId>
      {"\n"}
      <SqlKw>ORDER BY</SqlKw> <SqlId>visits</SqlId> <SqlKw>DESC</SqlKw>
      {"\n"}
      <SqlKw>LIMIT</SqlKw> <SqlNum>5</SqlNum>
    </MockSqlSurface>
  );
}

function MockSqlUserActionsQuery() {
  return (
    <MockSqlSurface className="text-[11px]">
      <SqlKw>SELECT</SqlKw>
      {"\n  "}
      <SqlFn>toStartOfHour</SqlFn>(<SqlId>event_time</SqlId>) <SqlKw>AS</SqlKw> <SqlId>hour</SqlId>,{"\n  "}
      <SqlFn>count</SqlFn>() <SqlKw>AS</SqlKw> <SqlId>events</SqlId>,{"\n  "}
      <SqlFn>avg</SqlFn>(<SqlId>duration_ms</SqlId>) <SqlKw>AS</SqlKw> <SqlId>avg_dur</SqlId>
      {"\n"}
      <SqlKw>FROM</SqlKw> <SqlId>user_actions</SqlId>
      {"\n"}
      <SqlKw>WHERE</SqlKw> <SqlId>event_date</SqlId> = <SqlFn>today</SqlFn>()
      {"\n"}
      <SqlKw>GROUP BY</SqlKw> <SqlId>hour</SqlId>
      {"\n"}
      <SqlKw>ORDER BY</SqlKw> <SqlId>hour</SqlId>
    </MockSqlSurface>
  );
}

// ─── Static data for mocks ──────────────────────────────────────────────────

const MOCK_RESULTS = [
  { domain: "shop.example.com", visits: "1,248,301", users: "342,109" },
  { domain: "blog.example.com", visits: "891,024", users: "215,847" },
  { domain: "docs.example.com", visits: "654,112", users: "189,302" },
  { domain: "api.example.com", visits: "423,891", users: "12,045" },
];

// Demo roadmap areas mimicking real RoadmapArea structure
const DEMO_AREAS = [
  {
    id: "basics",
    label: "Основы ClickHouse с нуля",
    description: "Пользователь не имеет опыта работы с ClickHouse, поэтому необходимо освоить базовые принципы и архитектуру.",
    topics: [
      { id: "columnar_storage", label: "Колоночное хранение данных", status: "to_learn" as const },
      { id: "mergetree_basics", label: "Основы MergeTree", status: "to_learn" as const },
      { id: "basic_select", label: "SELECT и базовый SQL", status: "to_learn" as const },
    ],
  },
  {
    id: "querying",
    label: "Эффективное извлечение данных",
    description: "Пользователь знаком с CTE и PREWHERE, что является основой для оптимизации запросов.",
    topics: [
      { id: "prewhere", label: "PREWHERE для фильтрации данных", status: "known" as const },
      { id: "cte", label: "CTE для структурирования запросов", status: "known" as const },
      { id: "group_by", label: "GROUP BY для агрегации", status: "next" as const },
      { id: "agg_functions", label: "Агрегатные функции для анализа", status: "to_learn" as const },
      { id: "joins", label: "JOIN для объединения таблиц", status: "to_learn" as const },
    ],
  },
  {
    id: "data_modeling",
    label: "Моделирование данных для разработчика",
    description: "Как разработчику, пользователю важно понимать, как эффективно хранить и обновлять данные.",
    topics: [
      { id: "partitioning", label: "Партиционирование", status: "to_learn" as const },
      { id: "sorting_key", label: "Ключ сортировки", status: "to_learn" as const },
      { id: "materialized_views", label: "Материализованные представления", status: "to_learn" as const },
    ],
  },
  {
    id: "optimization",
    label: "Оптимизация запросов и производительности",
    description: "Для карьерного роста необходимо научиться оптимизировать запросы и понимать внутреннее устройство.",
    topics: [
      { id: "explain", label: "EXPLAIN и профилирование", status: "to_learn" as const },
      { id: "indexing", label: "Индексы и гранулы", status: "to_learn" as const },
    ],
  },
  {
    id: "production",
    label: "Подготовка к производственной среде",
    description: "Пользователь тестирует локально, но для карьеры нужно понимание масштабирования и отказоустойчивости.",
    topics: [
      { id: "replication", label: "Репликация и шардирование", status: "to_learn" as const },
      { id: "monitoring", label: "Мониторинг и диагностика", status: "to_learn" as const },
    ],
  },
];

// ─── Landing Page ───────────────────────────────────────────────────────────

export function Landing() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [showQuiz, setShowQuiz] = useState(false);
  const [aiTutorTab, setAiTutorTab] = useState(0);
  const [contactType, setContactType] = useState<"email" | "telegram">("email");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    telegram: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [authorImageError, setAuthorImageError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    if (!formData.email && !formData.telegram) {
      setSubmitStatus({ type: "error", message: "Укажите email или Telegram для связи" });
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        company: formData.company || undefined,
        message: formData.message,
        ...(contactType === "email" ? { email: formData.email } : { telegram: formData.telegram }),
      };

      const response = await fetch(`${API_HOST}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({ type: "success", message: "Сообщение успешно отправлено! Мы свяжемся с вами в ближайшее время." });
        setFormData({ name: "", email: "", telegram: "", company: "", message: "" });
        setContactType("email");
      } else {
        setSubmitStatus({ type: "error", message: data.message || "Ошибка при отправке сообщения" });
      }
    } catch {
      setSubmitStatus({ type: "error", message: "Ошибка при отправке сообщения. Попробуйте позже." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartQuiz = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setShowQuiz(true);
  };

  return (
    <div className="w-full">
      <SEOHead
        title="Quantile Camp — Онлайн-курсы по ClickHouse и SQL | Интерактивное обучение"
        description="Изучайте ClickHouse и SQL на практике. 50+ заданий, интерактивный тренажёр, AI-тьютор. Работайте с реальными данными без установки. Бесплатный курс ClickHouse Basics."
        keywords="ClickHouse обучение, SQL тренажёр, курсы ClickHouse, онлайн обучение SQL, интерактивный SQL, базы данных обучение, аналитика данных, Data Engineering, ClickHouse курсы бесплатно, SQL практика, ClickHouse tutorial, обучение программированию, Big Data обучение, ClickHouse для начинающих, SQL запросы, ClickHouse кластер, аналитические базы данных"
        structuredData={[generateWebSiteSchema(), generateOrganizationSchema()]}
      />

      {/* ─── HERO ─────────────────────────────────────────────────────────── */}
      <section className="pt-28 pb-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-primary/30 bg-primary/5 text-primary text-sm font-medium mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                Каждая вторая Data-вакансия требует ClickHouse
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-heading font-bold text-neutral-text leading-tight tracking-tight mb-5">
                Освойте ClickHouse на&nbsp;практике с&nbsp;AI&#8209;тьютором
              </h1>
              <p className="text-lg text-neutral-text/70 leading-relaxed mb-8 max-w-xl">
                Не знаете с чего начать? Мы решили эту проблему. Структурированные курсы,
                интерактивный тренажёр и персональный AI&#8209;тьютор — прямо в браузере,
                без установки и настройки.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <Link
                  to="/simulator"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition font-semibold shadow-lg shadow-primary/20 hover:shadow-xl"
                >
                  <PlayCircle className="w-5 h-5" />
                  Начать бесплатно
                </Link>
                <button
                  type="button"
                  onClick={handleStartQuiz}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-primary border-2 border-primary/30 rounded-xl hover:border-primary/60 hover:bg-primary/5 transition font-semibold"
                >
                  <Brain className="w-5 h-5" />
                  Пройти тест знаний
                </button>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-neutral-text/60">
                <span className="flex items-center gap-1.5">
                  <Target className="w-4 h-4 text-primary" />
                  50+ заданий
                </span>
                <span className="flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4 text-primary" />
                  AI Tutor 24/7
                </span>
                <span className="flex items-center gap-1.5">
                  <Terminal className="w-4 h-4 text-primary" />
                  100% в браузере
                </span>
              </div>
            </motion.div>

            {/* Right — mock simulator */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="hidden lg:block"
            >
              <MockBrowserFrame>
                <div className="grid grid-cols-[1fr_1fr] min-h-[340px]">
                  {/* Editor — как в тренажёре: белый фон, зелёная подсветка */}
                  <div className="bg-white p-4 border-r border-neutral-border/30">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] uppercase tracking-wider text-neutral-text/45 font-mono">SQL Editor</span>
                      <span className="ml-auto px-2 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-mono">0.12 сек</span>
                    </div>
                    <MockSqlHitsQuery />
                  </div>
                  {/* Results */}
                  <div className="bg-white p-4">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-text/40 font-mono block mb-3">Результат</span>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-neutral-border">
                          <th className="text-left py-1.5 text-neutral-text/50 font-medium">domain</th>
                          <th className="text-right py-1.5 text-neutral-text/50 font-medium">visits</th>
                          <th className="text-right py-1.5 text-neutral-text/50 font-medium">users</th>
                        </tr>
                      </thead>
                      <tbody>
                        {MOCK_RESULTS.map((r, i) => (
                          <tr key={i} className="border-b border-neutral-border/40">
                            <td className="py-1.5 text-neutral-text font-mono text-[11px]">{r.domain}</td>
                            <td className="py-1.5 text-right text-neutral-text/70 font-mono text-[11px]">{r.visits}</td>
                            <td className="py-1.5 text-right text-neutral-text/70 font-mono text-[11px]">{r.users}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* AI Tutor floating */}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-primary/5 border-t border-primary/20">
                  <Brain className="w-4 h-4 text-primary" />
                  <span className="text-xs text-neutral-text/70">AI Tutor: Используй PREWHERE вместо WHERE для ускорения фильтрации по дате</span>
                </div>
              </MockBrowserFrame>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── PAIN POINTS ──────────────────────────────────────────────────── */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-text mb-3">
              Знакомые проблемы?
            </h2>
            <p className="text-neutral-text/60 text-lg max-w-xl mx-auto">
              Мы создали платформу, которая решает каждую из них
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: <HelpCircle className="w-6 h-6" />,
                title: "Не знаю с чего начать",
                desc: "Нет опыта с ClickHouse, документация огромная, непонятно что учить первым.",
                solution: "AI строит персональный план",
              },
              {
                icon: <BookX className="w-6 h-6" />,
                title: "Нет стандарта изучения",
                desc: "Документация разрозненная, статьи устарели, единой структуры обучения не существует.",
                solution: "Курсы дают чёткую структуру",
              },
              {
                icon: <Server className="w-6 h-6" />,
                title: "Сложно развернуть для практики",
                desc: "Установка кластера, загрузка данных, настройка окружения — всё это отвлекает от обучения.",
                solution: "Всё работает в браузере",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl p-6 border border-neutral-border shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="w-12 h-12 rounded-xl bg-red-50 text-red-400 flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="text-base font-semibold text-neutral-text mb-2">{item.title}</h3>
                <p className="text-sm text-neutral-text/60 leading-relaxed mb-4">{item.desc}</p>
                <div className="flex items-center gap-2 text-sm font-medium text-primary">
                  <CheckCircle2 className="w-4 h-4" />
                  {item.solution}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-text mb-3">
              Как это работает
            </h2>
            <p className="text-neutral-text/60 text-lg max-w-xl mx-auto">
              От первого вопроса до экспертного уровня — за 5 шагов
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              { icon: <Brain className="w-6 h-6" />, title: "Пройди тест", desc: "Ответь на 5 вопросов о своём опыте и целях" },
              { icon: <GitBranch className="w-6 h-6" />, title: "Получи роадмап", desc: "AI построит персональный план обучения" },
              { icon: <GraduationCap className="w-6 h-6" />, title: "Учись с курсами", desc: "Проходи структурированные курсы по ClickHouse" },
              { icon: <Terminal className="w-6 h-6" />, title: "Решай в тренажёре", desc: "Практикуй SQL на реальной базе ClickHouse" },
              { icon: <MessageSquare className="w-6 h-6" />, title: "AI Tutor везде", desc: "Получай помощь на каждой странице платформы" },
            ].map((step, i) => (
              <motion.div
                key={i}
                className="relative flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="relative mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                    {step.icon}
                  </div>
                  <span className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center shadow-md">
                    {i + 1}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-neutral-text mb-1">{step.title}</h3>
                <p className="text-sm text-neutral-text/60">{step.desc}</p>
                {/* Connector arrow (desktop) */}
                {i < 4 && (
                  <ArrowRight className="hidden lg:block absolute top-8 -right-[18px] w-6 h-6 text-primary/50" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── AI TUTOR SHOWCASE ────────────────────────────────────────────── */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-text mb-3">
              AI Tutor — помощник на каждой странице
            </h2>
            <p className="text-neutral-text/60 text-lg max-w-2xl mx-auto">
              Контекстно-зависимый AI-ассистент, который понимает что вы изучаете и помогает именно с этим
            </p>
          </motion.div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {["В тренажёре", "В курсах", "Отдельный чат"].map((label, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setAiTutorTab(i)}
                className={`px-3 py-2 sm:px-5 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition ${
                  aiTutorTab === i
                    ? "bg-primary text-white shadow-md"
                    : "bg-white text-neutral-text/60 hover:text-neutral-text border border-neutral-border"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab content mocks */}
          <motion.div
            key={aiTutorTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="max-w-4xl mx-auto"
          >
            <MockBrowserFrame>
              {aiTutorTab === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] min-h-[300px]">
                  {/* Editor side — белый редактор как в приложении */}
                  <div className="bg-white p-4 border-b md:border-b-0 md:border-r border-neutral-border/30">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-text/45 font-mono block mb-3">
                      Задача: Топ доменов по посещениям
                    </span>
                    <MockSqlHitsQuery />
                    <div className="mt-4 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20">
                      <span className="text-[10px] text-primary font-mono">Запрос выполнен за 0.12 сек, 4 строки</span>
                    </div>
                  </div>
                  {/* AI Tutor panel */}
                  <div className="bg-white border-t md:border-t-0 md:border-l-2 border-primary/30 shadow-[-4px_0_15px_rgba(10,191,83,0.08)] flex flex-col">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-border">
                      <Brain className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold text-neutral-text">AI Tutor</span>
                    </div>
                    <div className="flex-1 p-4 space-y-3">
                      <div className="bg-neutral-light rounded-xl p-3 text-xs text-neutral-text/70 leading-relaxed">
                        Как ускорить этот запрос?
                      </div>
                      <div className="bg-primary/5 rounded-xl p-3 text-xs text-neutral-text/80 leading-relaxed">
                        Используй <span className="font-mono bg-primary/10 px-1 rounded">PREWHERE</span> для фильтрации по дате — ClickHouse прочитает только нужные parts.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {aiTutorTab === 1 && (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] min-h-[300px]">
                  {/* Lesson side */}
                  <div className="bg-white p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="w-4 h-4 text-primary" />
                      <span className="text-xs text-neutral-text/50 font-medium">ClickHouse Basics / Модуль 2</span>
                    </div>
                    <h4 className="text-base font-semibold text-neutral-text mb-3">Движки таблиц: MergeTree</h4>
                    <p className="text-sm text-neutral-text/60 leading-relaxed mb-4">
                      MergeTree — основной движок хранения в ClickHouse. Он поддерживает индексацию по первичному ключу,
                      партиционирование и репликацию данных...
                    </p>
                    <MockSqlSurface className="text-[11px]">
                      <SqlKw>CREATE TABLE</SqlKw> <SqlId>hits</SqlId> (<br />
                      &nbsp;&nbsp;<SqlId>date</SqlId> Date,<br />
                      &nbsp;&nbsp;<SqlId>domain</SqlId> String,<br />
                      &nbsp;&nbsp;<SqlId>user_id</SqlId> UInt32<br />
                      ) <SqlKw>ENGINE</SqlKw> = <SqlFn>MergeTree</SqlFn>()<br />
                      <SqlKw>ORDER BY</SqlKw> (<SqlId>date</SqlId>, <SqlId>domain</SqlId>)
                    </MockSqlSurface>
                  </div>
                  {/* AI Tutor panel */}
                  <div className="bg-white border-t md:border-t-0 md:border-l-2 border-primary/30 shadow-[-4px_0_15px_rgba(10,191,83,0.08)] flex flex-col">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-border">
                      <Brain className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold text-neutral-text">AI Tutor</span>
                    </div>
                    <div className="flex-1 p-4 space-y-3">
                      <div className="bg-neutral-light rounded-xl p-3 text-xs text-neutral-text/70 leading-relaxed">
                        Чем MergeTree отличается от ReplacingMergeTree?
                      </div>
                      <div className="bg-primary/5 rounded-xl p-3 text-xs text-neutral-text/80 leading-relaxed">
                        ReplacingMergeTree автоматически дедуплицирует строки с одинаковым ключом сортировки при фоновых merge. Используй его, когда данные могут приходить с дубликатами.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {aiTutorTab === 2 && (
                <div className="min-h-[300px] bg-white flex flex-col">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-border">
                    <Brain className="w-4 h-4 text-primary" />
                    <span className="text-sm font-semibold text-neutral-text">AI Tutor — свободный чат</span>
                  </div>
                  <div className="flex-1 p-6 space-y-4 max-w-2xl mx-auto w-full">
                    <div className="bg-neutral-light rounded-xl p-4 text-sm text-neutral-text/70 leading-relaxed">
                      Как устроена репликация в ClickHouse? Зачем нужен ZooKeeper?
                    </div>
                    <div className="bg-primary/5 rounded-xl p-4 text-sm text-neutral-text/80 leading-relaxed">
                      ClickHouse использует ZooKeeper (или ClickHouse Keeper) для координации репликации.
                      Каждая реплика хранит полную копию данных, а ZK отвечает за мета-информацию: какие parts уже скачаны, какие нужно синхронизировать.
                      <br /><br />
                      Начиная с версии 22.3+ можно использовать встроенный <span className="font-mono bg-primary/10 px-1 rounded">clickhouse-keeper</span> вместо отдельного ZooKeeper.
                    </div>
                  </div>
                  <div className="px-6 pb-4">
                    <div className="flex items-center gap-2 border border-neutral-border rounded-xl px-4 py-3 bg-neutral-light/50">
                      <span className="text-sm text-neutral-text/30 flex-1">Задай вопрос о ClickHouse...</span>
                      <Send className="w-4 h-4 text-primary/40" />
                    </div>
                  </div>
                </div>
              )}
            </MockBrowserFrame>
          </motion.div>
        </div>
      </section>

      {/* ─── INTERACTIVE ROADMAP DEMO ─────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-b from-white to-neutral-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left — text + CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:sticky lg:top-32"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-neutral-text mb-4">
                Персональный план обучения
              </h2>
              <p className="text-neutral-text/60 text-base leading-relaxed mb-4">
                AI анализирует твои ответы и строит карту знаний с 7&nbsp;областями и 34&nbsp;концепциями ClickHouse.
                Ты видишь что уже знаешь, что учить следующим и к чему стремиться.
              </p>
              <p className="text-neutral-text/60 text-base leading-relaxed mb-8">
                Результаты сохраняются и используются AI Tutor во всех ответах —
                он не будет объяснять то, что ты уже знаешь.
              </p>
              <button
                type="button"
                onClick={handleStartQuiz}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition font-semibold shadow-lg shadow-primary/20 mb-3"
              >
                <Brain className="w-5 h-5" />
                Построй свой роадмап
              </button>
              <p className="text-xs text-neutral-text/40">Занимает ~2 минуты</p>
            </motion.div>

            {/* Right — static roadmap mock */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="rounded-2xl border border-neutral-border bg-white shadow-lg overflow-hidden">
                {/* Summary header */}
                <div className="px-4 py-3 border-b border-neutral-border/40 bg-gray-50/60">
                  <p className="text-[11px] text-neutral-text/60 leading-relaxed">
                    Вы начинающий пользователь ClickHouse с базовым пониманием архитектуры,
                    некоторых движков хранения и элементов запросов.
                  </p>
                </div>
                {/* Area accordion */}
                <div className="px-3 py-3 space-y-2">
                  {DEMO_AREAS.map((area, areaIdx) => {
                    const isExpanded = areaIdx === 1; // "Эффективное извлечение данных" expanded
                    return (
                      <div key={area.id} className="border border-neutral-border/40 rounded-xl overflow-hidden">
                        {/* Area header */}
                        <div className="w-full flex items-start gap-2.5 px-3 py-2.5 bg-white text-left">
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-semibold text-neutral-text leading-snug">{area.label}</span>
                            <p className="text-[11px] text-neutral-text/50 mt-0.5 leading-snug">{area.description}</p>
                          </div>
                          <div className="flex-shrink-0 mt-0.5">
                            {isExpanded
                              ? <ChevronUp className="w-3.5 h-3.5 text-neutral-text/40" />
                              : <ChevronDown className="w-3.5 h-3.5 text-neutral-text/40" />
                            }
                          </div>
                        </div>
                        {/* Topics list */}
                        {isExpanded && (
                          <div className="border-t border-neutral-border/40 bg-gray-50/40 px-3 py-2 space-y-1">
                            {area.topics.map((topic) => (
                              <div
                                key={topic.id}
                                className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg"
                              >
                                <div className={`w-2.5 h-2.5 rounded-full border-2 flex-shrink-0 ${
                                  topic.status === "known"
                                    ? "bg-green-500 border-green-500"
                                    : topic.status === "next"
                                    ? "bg-blue-500 border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.2)]"
                                    : "bg-white border-gray-300"
                                }`} />
                                <span className={`text-xs flex-1 ${
                                  topic.status === "known" ? "text-green-700" : "text-neutral-text/70"
                                }`}>
                                  {topic.label}
                                </span>
                                {topic.status === "next" && (
                                  <span className="text-[10px] font-semibold text-blue-500 bg-blue-50 rounded-full px-1.5 py-0.5 flex-shrink-0">
                                    Изучить
                                  </span>
                                )}
                                {topic.status === "known" && (
                                  <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── SIMULATOR FEATURE ────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left — mock */}
            <motion.div
              className="order-2 lg:order-1"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <MockBrowserFrame>
                <div className="grid grid-cols-2 min-h-[280px]">
                  <div className="bg-white p-4 border-r border-neutral-border/30">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] uppercase tracking-wider text-neutral-text/45 font-mono">Редактор</span>
                    </div>
                    <MockSqlUserActionsQuery />
                  </div>
                  <div className="bg-white p-4">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="border-b border-neutral-border">
                          <th className="text-left py-1 text-neutral-text/50 font-medium">hour</th>
                          <th className="text-right py-1 text-neutral-text/50 font-medium">events</th>
                          <th className="text-right py-1 text-neutral-text/50 font-medium">avg_dur</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        {[
                          ["2024-01-15 10:00", "12,847", "234"],
                          ["2024-01-15 11:00", "15,392", "198"],
                          ["2024-01-15 12:00", "18,201", "312"],
                          ["2024-01-15 13:00", "14,523", "287"],
                        ].map(([h, e, d], i) => (
                          <tr key={i} className="border-b border-neutral-border/30">
                            <td className="py-1 text-neutral-text/70">{h}</td>
                            <td className="py-1 text-right text-neutral-text/60">{e}</td>
                            <td className="py-1 text-right text-neutral-text/60">{d}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </MockBrowserFrame>
            </motion.div>

            {/* Right — text (shown first on mobile) */}
            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-text mb-5">
                Тренажёр с реальным ClickHouse
              </h2>
              <div className="space-y-4 mb-8">
                {[
                  "Пишите SQL и мгновенно видите результат выполнения",
                  "50+ заданий из e-commerce, логистики и HR",
                  "Реальная база ClickHouse, а не симуляция",
                  "Подсказки от AI Tutor прямо в редакторе",
                  "Метрики производительности: время, память, CPU",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-text/70 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/simulator"
                className="inline-flex items-center gap-2 text-primary hover:text-primary-dark font-semibold group"
              >
                Попробовать тренажёр
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── COURSES ──────────────────────────────────────────────────────── */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-3 text-neutral-text">Курсы</h2>
            <p className="text-neutral-text/60 text-lg max-w-xl mx-auto">
              Структурированное обучение от основ до продвинутых тем
            </p>
          </motion.div>

          {/* Main course card */}
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-primary mb-8 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="text-xl font-heading font-bold text-neutral-text">ClickHouse Basics</h3>
                  <span className="px-2.5 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full border border-green-200">
                    Бесплатный
                  </span>
                </div>
                <p className="text-sm text-neutral-text/50">Базовый уровень</p>
              </div>
            </div>
            <p className="text-neutral-text/70 mb-6 leading-relaxed">
              Пошаговый курс, который проведёт через ключевые концепции ClickHouse: архитектура кластера,
              движки таблиц, запросы и моделирование данных. С интерактивными заданиями и AI-тьютором.
            </p>
            <Link
              to="/courses/clickhouse-basics"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition font-semibold text-sm shadow-sm"
            >
              Начать курс бесплатно
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {/* Coming soon */}
          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {[
              { title: "ClickHouse Advanced", desc: "Оптимизация, партиции, репликация" },
              { title: "ClickHouse для BI", desc: "Дашборды, метрики, визуализация" },
              { title: "Performance Tuning", desc: "Профилирование и тюнинг запросов" },
            ].map((c, i) => (
              <motion.div
                key={i}
                className="bg-neutral-bg rounded-xl p-5 border border-neutral-border opacity-60"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 0.6, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <h4 className="text-sm font-semibold text-neutral-text/60 mb-1">{c.title}</h4>
                <p className="text-xs text-neutral-text/40">{c.desc}</p>
                <span className="text-[10px] text-neutral-text/30 mt-2 block">Скоро</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CORPORATE TRAINING ───────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-3 text-neutral-text">
              Корпоративное обучение и консалтинг
            </h2>
            <p className="text-neutral-text/60 text-lg max-w-2xl mx-auto">
              Повысьте квалификацию вашей команды с помощью наших программ
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              className="bg-neutral-light rounded-2xl p-8 border border-neutral-border"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-neutral-text">Корпоративное обучение</h3>
                  <p className="text-sm text-neutral-text/50">Для команд и компаний</p>
                </div>
              </div>
              <p className="text-neutral-text/70 mb-6 leading-relaxed text-sm">
                Организуем обучение вашей команды работе с ClickHouse и SQL. Программы адаптированы под специфику вашего бизнеса.
              </p>
              <div className="space-y-2.5 mb-6">
                {["Индивидуальные программы обучения", "Практические задания на ваших данных", "Гибкий график и формат занятий", "Сертификаты об окончании курса"].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-text/70">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-6">
                <div className="flex items-center gap-2 text-primary font-medium text-sm">
                  <Users className="w-4 h-4" />
                  <span>От 5 до 100+ сотрудников</span>
                </div>
                <Link
                  to="/corporate"
                  className="text-sm font-semibold text-neutral-text hover:text-primary transition-colors"
                >
                  Подробнее →
                </Link>
              </div>
            </motion.div>

            <motion.div
              className="bg-neutral-light rounded-2xl p-8 border border-neutral-border"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Briefcase className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-neutral-text">Консалтинг</h3>
                  <p className="text-sm text-neutral-text/50">Экспертная поддержка</p>
                </div>
              </div>
              <p className="text-neutral-text/70 mb-6 leading-relaxed text-sm">
                Получите экспертную помощь в проектировании архитектуры данных, оптимизации запросов и настройке кластеров ClickHouse.
              </p>
              <div className="space-y-2.5 mb-6">
                {["Архитектура и проектирование систем", "Оптимизация производительности", "Миграция и интеграция данных", "Code review и best practices"].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-text/70">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-6">
                <div className="flex items-center gap-2 text-primary font-medium text-sm">
                  <Target className="w-4 h-4" />
                  <span>Индивидуальный подход</span>
                </div>
                <Link
                  to="/consulting"
                  className="text-sm font-semibold text-neutral-text hover:text-primary transition-colors"
                >
                  Подробнее →
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── AUTHOR ───────────────────────────────────────────────────────── */}
      <section className="py-16 bg-neutral-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 border-2 border-primary/20 shadow-md overflow-hidden">
                {!authorImageError ? (
                  <img
                    src="/author.jpg"
                    alt="Валентин Борисов"
                    className="w-full h-full object-cover"
                    width="96"
                    height="96"
                    loading="lazy"
                    decoding="async"
                    onError={() => setAuthorImageError(true)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Database className="w-10 h-10 text-primary/60" />
                  </div>
                )}
              </div>
            </div>
            <div className="text-center sm:text-left">
              <h3 className="text-lg font-heading font-bold text-neutral-text mb-1">Валентин Борисов</h3>
              <p className="text-sm text-neutral-text/60 leading-relaxed mb-2">
                Эксперт по ClickHouse. Работал в крупнейших BigTech-компаниях РФ.
                Преподаёт Data Engineering в магистратуре Центрального Университета.
                Создатель Quantile Camp.
              </p>
              <a
                href="https://t.me/napit_ok"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary-dark font-medium transition"
              >
                <Send className="w-4 h-4" />
                @napit_ok
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── CONTACT FORM ─────────────────────────────────────────────────── */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-heading font-bold mb-3 text-neutral-text">Свяжитесь с нами</h2>
            <p className="text-neutral-text/60">
              Есть вопросы по обучению, корпоративным программам или сотрудничеству?
            </p>
          </div>

          <div className="bg-neutral-light rounded-2xl p-6 sm:p-8 border border-neutral-border">
            <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2 sm:gap-5">
              <input
                type="text"
                placeholder="Ваше имя"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="col-span-2 sm:col-span-1 px-4 py-3 border border-neutral-border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-neutral-text text-sm"
                required
              />
              <div className="col-span-2 sm:col-span-1 flex gap-2">
                <button
                  type="button"
                  onClick={() => { setContactType("email"); setFormData({ ...formData, telegram: "" }); }}
                  className={`flex-1 px-4 py-3 border rounded-xl text-sm transition ${
                    contactType === "email"
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-neutral-text border-neutral-border hover:bg-neutral-light"
                  }`}
                >
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => { setContactType("telegram"); setFormData({ ...formData, email: "" }); }}
                  className={`flex-1 px-4 py-3 border rounded-xl text-sm transition ${
                    contactType === "telegram"
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-neutral-text border-neutral-border hover:bg-neutral-light"
                  }`}
                >
                  Telegram
                </button>
              </div>
              {contactType === "email" ? (
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="col-span-2 px-4 py-3 border border-neutral-border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-neutral-text text-sm"
                  required={contactType === "email"}
                />
              ) : (
                <input
                  type="text"
                  placeholder="Telegram username (например: @username)"
                  value={formData.telegram}
                  onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
                  className="col-span-2 px-4 py-3 border border-neutral-border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-neutral-text text-sm"
                  required={contactType === "telegram"}
                />
              )}
              <input
                type="text"
                placeholder="Компания / Организация"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="col-span-2 px-4 py-3 border border-neutral-border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-neutral-text text-sm"
              />
              <textarea
                placeholder="Сообщение"
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="col-span-2 px-4 py-3 border border-neutral-border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-neutral-text text-sm resize-none"
                required
              />
              {submitStatus && (
                <div className={`col-span-2 px-4 py-3 rounded-xl text-sm ${
                  submitStatus.type === "success"
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                }`}>
                  {submitStatus.message}
                </div>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="col-span-2 bg-primary text-white rounded-xl px-6 py-3 hover:bg-primary-dark transition font-semibold text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Отправка..." : "Отправить"}
              </button>
            </form>
            <p className="text-neutral-text/40 text-xs mt-4 text-center">
              Мы свяжемся с вами в течение 2 рабочих дней.
            </p>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ────────────────────────────────────────────────────── */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-neutral-text">
              Готовы начать обучение?
            </h2>
            <p className="text-neutral-text/60 text-lg mb-8">
              Присоединяйтесь к студентам, которые уже изучают ClickHouse с Quantile Camp
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/simulator"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition font-semibold shadow-lg shadow-primary/20"
              >
                <PlayCircle className="w-5 h-5" />
                Начать бесплатно
              </Link>
              <Link
                to="/courses"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-primary border-2 border-primary/30 rounded-xl hover:border-primary/60 hover:bg-primary/5 transition font-semibold"
              >
                <BookOpen className="w-5 h-5" />
                Посмотреть курсы
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── FOOTER ───────────────────────────────────────────────────────── */}
      <footer className="py-12 bg-white border-t border-neutral-border">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="font-heading font-bold text-neutral-text mb-4">Quantile Camp</h3>
              <p className="text-neutral-text/50 text-sm leading-relaxed">
                Интерактивная платформа для изучения ClickHouse и SQL.
                Учитесь через практику, без установки и настройки.
              </p>
            </div>
            <div>
              <h3 className="font-heading font-bold text-neutral-text mb-4">Навигация</h3>
              <ul className="space-y-2">
                <li><Link to="/courses" className="text-neutral-text/50 hover:text-primary text-sm transition">Курсы</Link></li>
                <li><Link to="/simulator" className="text-neutral-text/50 hover:text-primary text-sm transition">Тренажёр</Link></li>
                <li><Link to="/ai-tutor" className="text-neutral-text/50 hover:text-primary text-sm transition">AI Tutor</Link></li>
                <li><Link to="/faq" className="text-neutral-text/50 hover:text-primary text-sm transition">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-heading font-bold text-neutral-text mb-4">Контакты</h3>
              <p className="text-neutral-text/50 text-sm mb-2">
                Telegram: <a href="https://t.me/napit_ok" className="text-primary hover:text-primary-dark transition">@napit_ok</a>
              </p>
              <p className="text-neutral-text/50 text-sm">
                Есть вопросы? <a href="#contact" className="text-primary hover:text-primary-dark transition">Напишите нам</a>
              </p>
            </div>
          </div>
          <div className="pt-8 border-t border-neutral-border flex flex-col sm:flex-row items-center justify-between gap-2 text-neutral-text/40 text-sm">
            <span>&copy; {new Date().getFullYear()} Quantile Camp. Все права защищены.</span>
            <div className="flex gap-4">
              <Link to="/privacy" className="hover:text-primary transition text-xs">Политика конфиденциальности</Link>
              <Link to="/terms" className="hover:text-primary transition text-xs">Условия использования</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* ─── QUIZ MODAL ───────────────────────────────────────────────────── */}
      {showQuiz && user && (
        <AssessmentQuizModal
          userId={user.id}
          onClose={() => setShowQuiz(false)}
          onComplete={() => setShowQuiz(false)}
        />
      )}
    </div>
  );
}
